import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON as GeoJSONLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { GeoJsonObject } from 'geojson';
import 'leaflet/dist/leaflet.css';
import { Box, CircularProgress, Typography } from '@mui/material';
import { fetchOnemapLandLot } from '../../services/api';

const ONEMAP_TILE = 'https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png';
const ONEMAP_ATTR =
  '<a href="https://www.onemap.gov.sg/" target="_blank" rel="noreferrer">OneMap</a> © SLA';

type LotGeoJson = { type: string; coordinates: unknown };

function isPolygonalGeoJson(g: LotGeoJson | null): g is LotGeoJson {
  return g != null && (g.type === 'Polygon' || g.type === 'MultiPolygon');
}

function MapLotViewController({
  center,
  geojsonData,
}: {
  center: [number, number];
  geojsonData: LotGeoJson | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (geojsonData && isPolygonalGeoJson(geojsonData)) {
      try {
        const layer = L.geoJSON(geojsonData as GeoJsonObject);
        const b = layer.getBounds();
        if (b.isValid()) {
          map.fitBounds(b, { padding: [28, 28], maxZoom: 18 });
          return;
        }
      } catch {
        /* fall through */
      }
    }
    map.setView(center, 16);
  }, [map, center, geojsonData]);
  return null;
}

interface ProjectLandLotMapProps {
  latitude: number;
  longitude: number;
  projectName: string;
  /** Map viewport height; defaults to the site-plan detail panel size. */
  height?: string | number;
  /** Show the lot number / ownership badge (top-left). Off for compact thumbnails. */
  showLotLabel?: boolean;
  /** When false, map is display-only (no pan/zoom/popups); clicks pass through to parent. */
  interactive?: boolean;
}

const DEFAULT_MAP_HEIGHT = 'min(600px, 65vh)';

const ProjectLandLotMap: React.FC<ProjectLandLotMapProps> = ({
  latitude,
  longitude,
  projectName,
  height = DEFAULT_MAP_HEIGHT,
  showLotLabel = true,
  interactive = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [lotNumber, setLotNumber] = useState<string | null>(null);
  const [ownership, setOwnership] = useState<string | null>(null);
  const [geojson, setGeojson] = useState<LotGeoJson | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotice(null);
      try {
        const r = await fetchOnemapLandLot(latitude, longitude);
        if (cancelled) return;
        setLotNumber(r.lotNumber);
        setOwnership(r.ownership);
        if (r.geojson && isPolygonalGeoJson(r.geojson)) {
          setGeojson(r.geojson);
          setNotice(null);
        } else {
          setGeojson(null);
          setNotice(
            r.lotNumber
              ? 'Land lot found but boundary geometry is unavailable.'
              : 'No land lot found at this location.'
          );
        }
      } catch (e) {
        if (!cancelled) {
          setNotice(e instanceof Error ? e.message : 'Could not load land lot');
          setGeojson(null);
          setLotNumber(null);
          setOwnership(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude]);

  const center = useMemo((): [number, number] => [latitude, longitude], [latitude, longitude]);

  const polygonStyle = useMemo(
    () => ({
      color: 'var(--color-accent, #f57c00)',
      weight: 2,
      fillColor: 'var(--color-accent, #f57c00)',
      fillOpacity: 0.22,
    }),
    []
  );

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid rgba(var(--color-text-rgb), 0.12)',
        borderRadius: 1,
        overflow: 'hidden',
        height,
        ...(!interactive && {
          pointerEvents: 'none',
          '& .leaflet-container, & .leaflet-pane, & .leaflet-control-container': {
            pointerEvents: 'none',
          },
        }),
      }}
    >
      <MapContainer
        center={center}
        zoom={16}
        style={{ height: '100%', width: '100%' }}
        dragging={interactive}
        scrollWheelZoom={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        boxZoom={interactive}
        keyboard={interactive}
        zoomControl={interactive}
      >
        <TileLayer attribution={ONEMAP_ATTR} url={ONEMAP_TILE} />
        <MapLotViewController center={center} geojsonData={geojson} />
        {geojson && isPolygonalGeoJson(geojson) ? (
          <GeoJSONLayer
            key={lotNumber ?? 'land-lot'}
            data={geojson as GeoJsonObject}
            style={polygonStyle as L.PathOptions}
            onEachFeature={
              interactive
                ? (_feature, layer) => {
                    const lines = [lotNumber && `Lot: ${lotNumber}`, ownership && String(ownership)].filter(
                      Boolean,
                    );
                    if (lines.length) layer.bindPopup(lines.join('<br/>'));
                  }
                : undefined
            }
          />
        ) : null}
        <CircleMarker
          center={center}
          radius={6}
          pathOptions={{ color: '#1976d2', fillColor: '#1976d2', fillOpacity: 1, weight: 2 }}
          {...(interactive
            ? { children: <Popup>{projectName}</Popup> }
            : { interactive: false })}
        />
      </MapContainer>
      {notice ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.72)',
            color: '#fff',
            p: 1,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption">{notice}</Typography>
        </Box>
      ) : null}
      {!notice && lotNumber && showLotLabel ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: 'rgba(0,0,0,0.65)',
            color: '#fff',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {lotNumber}
          </Typography>
          {ownership ? (
            <Typography variant="caption" display="block">
              {ownership}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default ProjectLandLotMap;
